import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Analytics.css";
import Topbar from "../Global/topbar";
import {
  getPlatformAnalytics,
  getUserAnalytics,
  getConsultantAnalytics,
} from "../../services/api/analytics";
import { getConsultant } from "../../services/api/consultant";
import { getUsers } from "../../services/api/users";
import { CircularProgress, Skeleton, TextField, InputAdornment } from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const today = new Date().toISOString().split("T")[0];

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return "0";
  if (amount >= 10000000) return (amount / 10000000).toFixed(1).replace(/\.0$/, "") + "Cr";
  if (amount >= 100000) return (amount / 100000).toFixed(1).replace(/\.0$/, "") + "L";
  if (amount >= 1000) return (amount / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return Number(amount).toLocaleString("en-IN");
};

const formatNum = (val) => {
  if (val === undefined || val === null) return null;
  return Number(val).toLocaleString("en-IN");
};

const StatCard = ({ icon, label, value, sub, color = "blue", onClick }) => (
  <div
    className={`analytics-card ${color}${onClick ? " clickable" : ""}`}
    onClick={onClick}
    style={onClick ? { cursor: "pointer" } : {}}
  >
    <span className="card-icon">{icon}</span>
    <span className="card-label">{label}</span>
    <span className="card-value">
      {value !== null && value !== undefined ? value : <Skeleton animation="wave" width={60} />}
    </span>
    {sub && <span className="card-sub">{sub}</span>}
  </div>
);

// ─── Platform Cards ─────────────────────────────────────────────────────────
const PlatformCards = ({ d, view, date, onCardClick }) => {
  if (!d) return null;
  const totalRevenue = (d?.revenue_recharge || 0) + (d?.revenue_chat || 0) + (d?.revenue_calls || 0);

  return (
    <>
      <div className="analytics-section">
        <div className="analytics-cards" style={{ gridTemplateColumns: "1fr" }}>
          <div className="analytics-card green" style={{ textAlign: "center", borderLeft: "none", borderTop: "4px solid #10b981" }}>
            <span className="card-icon">💎</span>
            <span className="card-label">{view === "daily" ? `Total Revenue (${date})` : "All Time Revenue"}</span>
            <span className="card-value" style={{ fontSize: 32 }}>₹{formatAmount(totalRevenue)}</span>
          </div>
        </div>
      </div>
      <Section title="User Lifecycle" cards={[
        { icon: "👤", label: "New Users", value: formatNum(d?.signups_user), color: "blue", onClick: () => onCardClick("new_users", "New Users") },
        { icon: "🧑‍💼", label: "New Consultants", value: formatNum(d?.signups_consultant), color: "purple", onClick: () => onCardClick("new_consultants", "New Consultants") },
        { icon: "🟢", label: "Active Users", value: formatNum(d?.active_users), color: "green", onClick: () => onCardClick("active_users", "Active Users") },
        { icon: "🟣", label: "Active Consultants", value: formatNum(d?.active_consultants), color: "teal", onClick: () => onCardClick("active_consultants", "Active Consultants") },
        { icon: "🔑", label: "Total Logins", value: formatNum(d?.logins_total), color: "blue" },
        { icon: "🗑️", label: "Users Deleted", value: formatNum(d?.users_deleted), color: "red" },
      ]} />
      <Section title="Chat & Messaging" cards={[
        { icon: "💬", label: "Total Messages", value: formatNum(d?.messages_total), color: "blue" },
        { icon: "📝", label: "Text Messages", value: formatNum(d?.text_messages), color: "teal" },
        { icon: "🎤", label: "Audio Messages", value: formatNum(d?.audio_messages), color: "purple" },
        { icon: "🆕", label: "New Conversations", value: formatNum(d?.conversations_created), color: "green" },
        { icon: "📢", label: "Broadcasts", value: formatNum(d?.broadcasts_total), color: "orange" },
      ]} />
      <Section title="Calling" cards={[
        { icon: "📞", label: "Total Calls", value: formatNum(d?.calls_total), color: "blue", onClick: () => onCardClick("total_calls", "Total Calls") },
        { icon: "✅", label: "Answered", value: formatNum(d?.calls_answered), color: "green", sub: d?.calls_total > 0 ? `${Math.round((d?.calls_answered / d?.calls_total) * 100)}% answer rate` : "", onClick: () => onCardClick("calls_answered", "Answered Calls") },
        { icon: "📵", label: "Missed", value: formatNum(d?.calls_missed), color: "orange", onClick: () => onCardClick("calls_missed", "Missed Calls") },
        { icon: "❌", label: "Rejected", value: formatNum(d?.calls_rejected), color: "red", onClick: () => onCardClick("calls_rejected", "Rejected Calls") },
        { icon: "⏱️", label: "Auto Ended", value: formatNum(d?.calls_auto_ended), color: "pink" },
        { icon: "⏳", label: "Call Minutes", value: formatNum(d?.call_duration_minutes), color: "purple", sub: d?.calls_answered > 0 ? `Avg ${Math.round(d?.call_duration_minutes / d?.calls_answered)} min/call` : "" },
      ]} />
      <Section title="Revenue & Payments" cards={[
        { icon: "💰", label: "Recharge Revenue", value: `₹${formatAmount(d?.revenue_recharge)}`, color: "green", onClick: () => onCardClick("recharge_revenue", "Recharge Revenue") },
        { icon: "💬💵", label: "Chat Revenue", value: `₹${formatAmount(d?.revenue_chat)}`, color: "purple" },
        { icon: "📞💵", label: "Call Revenue", value: `₹${formatAmount(d?.revenue_calls)}`, color: "blue", onClick: () => onCardClick("call_revenue", "Call Revenue") },
        { icon: "🔄", label: "Total Recharges", value: formatNum(d?.recharges_total), color: "teal", onClick: () => onCardClick("total_recharges", "Total Recharges") },
        { icon: "⚠️", label: "Failed Recharges", value: formatNum(d?.recharges_failed), color: "red" },
        { icon: "🚫", label: "Penalties", value: formatNum(d?.penalties_total), color: "orange" },
      ]} />
      <Section title="Consultant Performance" cards={[
        { icon: "👁️", label: "Profile Views", value: formatNum(d?.profile_views_total), color: "blue" },
        { icon: "🎧", label: "Audio Listens", value: formatNum(d?.audio_listens_total), color: "purple" },
        { icon: "📋", label: "Complaints", value: formatNum(d?.complaints_total), color: "red" },
      ]} />
      <Section title="Consultant Onboarding" cards={[
        { icon: "📝", label: "Registrations", value: formatNum(d?.registrations_submitted), color: "blue" },
        { icon: "✅", label: "Approved", value: formatNum(d?.registrations_approved), color: "green" },
        { icon: "❌", label: "Rejected", value: formatNum(d?.registrations_rejected), color: "red" },
      ]} />
    </>
  );
};

// ─── Entity (Consultant / User) Cards ───────────────────────────────────────
const EntityCards = ({ d, entityType, view }) => {
  if (!d) return null;
  const isConsultant = entityType === "consultant";

  return (
    <>
      <Section title="Chat & Messaging" cards={[
        { icon: "💬", label: "Messages Sent", value: formatNum(d?.messages_sent || d?.messages_count), color: "blue" },
        { icon: "📝", label: "Text Messages", value: formatNum(d?.text_messages), color: "teal" },
        { icon: "🎤", label: "Audio Messages", value: formatNum(d?.audio_messages), color: "purple" },
        { icon: "🆕", label: "Conversations", value: formatNum(d?.conversations_created || d?.conversations_count), color: "green" },
        { icon: "💻", label: "Chat Sessions", value: formatNum(d?.chat_sessions), color: "blue" },
        ...(isConsultant ? [{ icon: "📢", label: "Broadcasts", value: formatNum(d?.broadcasts_sent), color: "orange" }] : []),
      ]} />
      <Section title="Calling" cards={[
        { icon: "📞", label: isConsultant ? "Calls Received" : "Calls Initiated", value: formatNum(isConsultant ? d?.calls_received : d?.calls_initiated), color: "blue" },
        { icon: "✅", label: "Answered", value: formatNum(d?.calls_answered), color: "green" },
        { icon: "📵", label: "Missed", value: formatNum(d?.calls_missed), color: "orange" },
        { icon: "❌", label: "Rejected", value: formatNum(d?.calls_rejected), color: "red" },
        { icon: "⏱️", label: "Auto Ended", value: formatNum(d?.calls_auto_ended), color: "pink" },
        { icon: "⏳", label: "Call Minutes", value: formatNum(d?.call_duration_minutes || d?.calls_duration_minutes), color: "purple" },
      ]} />
      <Section title={isConsultant ? "Earnings" : "Spending"} cards={
        isConsultant ? [
          { icon: "💬💵", label: "Earned (Chat)", value: `₹${formatAmount(d?.earned_from_chat)}`, color: "green" },
          { icon: "📞💵", label: "Earned (Calls)", value: `₹${formatAmount(d?.earned_from_calls)}`, color: "blue" },
          { icon: "💰", label: "Total Received", value: `₹${formatAmount(d?.payment_received)}`, color: "teal" },
          { icon: "🚫", label: "Penalties", value: formatNum(d?.penalties_count), color: "red" },
          { icon: "💸", label: "Penalty Amount", value: `₹${formatAmount(d?.penalties_amount)}`, color: "orange" },
        ] : [
          { icon: "💬💵", label: "Spent (Chat)", value: `₹${formatAmount(d?.spent_on_chat)}`, color: "purple" },
          { icon: "📞💵", label: "Spent (Calls)", value: `₹${formatAmount(d?.spent_on_calls)}`, color: "blue" },
          { icon: "💰", label: "Total Spent", value: `₹${formatAmount(d?.payment_spent)}`, color: "red" },
          { icon: "🔄", label: "Recharges", value: formatNum(d?.recharges_completed), color: "green" },
          { icon: "💎", label: "Recharged Amount", value: `₹${formatAmount(d?.recharge_amount)}`, color: "teal" },
        ]
      } />
      {isConsultant && (
        <Section title="Profile" cards={[
          { icon: "👁️", label: "Profile Views", value: formatNum(d?.profile_views), color: "blue" },
          { icon: "🎧", label: "Audio Listens", value: formatNum(d?.audio_listens), color: "purple" },
          { icon: "📋", label: "Complaints Received", value: formatNum(d?.complaints_received), color: "red" },
        ]} />
      )}
    </>
  );
};

// ─── Section Helper ─────────────────────────────────────────────────────────
const Section = ({ title, cards }) => (
  <div className="analytics-section">
    <div className="analytics-section-title">{title}</div>
    <div className="analytics-cards">
      {cards.map((c, i) => <StatCard key={i} {...c} onClick={c.onClick} />)}
    </div>
  </div>
);

// ─── Search Results Dropdown ────────────────────────────────────────────────
const SearchDropdown = ({ results, onSelect, loading }) => {
  if (!loading && results.length === 0) return null;
  return (
    <div className="analytics-search-dropdown">
      {loading ? (
        <div className="analytics-search-item" style={{ justifyContent: "center" }}>
          <CircularProgress size={20} />
        </div>
      ) : (
        results.map((item) => (
          <div
            key={item.id}
            className="analytics-search-item"
            onClick={() => onSelect(item)}
          >
            <span className="search-item-name">{item.name || item.google_name || "No Name"}</span>
            <span className="search-item-email">{item.email || item.phone_number || item.id}</span>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const Analytics = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(today);
  const [view, setView] = useState("daily");
  const [tab, setTab] = useState("platform"); // "platform" | "consultant" | "user"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleCardClick = useCallback((type, title) => {
    navigate("/admin/analytics/detail", {
      state: { type, date: view === "daily" ? date : null, title },
    });
  }, [navigate, date, view]);

  // Entity search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch platform data
  const fetchPlatform = useCallback(async (d) => {
    setLoading(true);
    try {
      const response = await getPlatformAnalytics(d);
      setData(response?.data?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch entity data
  const fetchEntityData = useCallback(async (entityId, entityType, d) => {
    setLoading(true);
    try {
      const fetcher = entityType === "consultant" ? getConsultantAnalytics : getUserAnalytics;
      const response = await fetcher(entityId, d);
      setData(response?.data?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // On tab / date change
  useEffect(() => {
    if (tab === "platform") {
      fetchPlatform(date);
    } else if (selectedEntity) {
      fetchEntityData(selectedEntity.id, tab, date);
    }
  }, [date, tab, selectedEntity, fetchPlatform, fetchEntityData]);

  // Search consultants/users
  const handleSearch = (query) => {
    setSearchQuery(query);
    setShowDropdown(true);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!query || query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        let response;
        if (tab === "consultant") {
          response = await getConsultant(query, "", 10);
        } else {
          response = await getUsers(query, 10);
        }
        const items = response?.data?.data?.data || response?.data?.data || [];
        setSearchResults(Array.isArray(items) ? items.slice(0, 10) : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  const handleEntitySelect = (item) => {
    setSelectedEntity(item);
    setSearchQuery(item.name || item.google_name || item.email || "");
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setData(null);
    setSelectedEntity(null);
    setSearchQuery("");
    setSearchResults([]);
    if (newTab === "platform") {
      fetchPlatform(date);
    }
  };

  const d = view === "daily" ? data?.daily : data?.lifetime;

  const entityLabel = tab === "consultant" ? "Consultant" : "User";

  return (
    <>
      <Topbar />
      <div className="analytics-page">
        {/* Header */}
        <div className="analytics-header">
          <h1>
            {tab === "platform" ? "Platform Analytics" :
             selectedEntity ? `${selectedEntity.name || selectedEntity.google_name || "N/A"} — ${entityLabel} Analytics` :
             `${entityLabel} Analytics`}
          </h1>
          <div className="analytics-date-row">
            <div className="analytics-toggle">
              <button className={view === "daily" ? "active" : ""} onClick={() => setView("daily")}>Daily</button>
              <button className={view === "lifetime" ? "active" : ""} onClick={() => setView("lifetime")}>Lifetime</button>
            </div>
            {view === "daily" && (
              <input
                type="date"
                className="analytics-date-input"
                value={date}
                max={today}
                onChange={(e) => setDate(e.target.value)}
              />
            )}
            <div style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              onClick={() => { setDate(today); setView("daily"); }}>
              <CachedIcon />
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="analytics-tab-bar">
          <button className={tab === "platform" ? "active" : ""} onClick={() => handleTabChange("platform")}>
            Platform
          </button>
          <button className={tab === "consultant" ? "active" : ""} onClick={() => handleTabChange("consultant")}>
            Consultant
          </button>
          <button className={tab === "user" ? "active" : ""} onClick={() => handleTabChange("user")}>
            User
          </button>
        </div>

        {/* Search bar for consultant/user */}
        {tab !== "platform" && (
          <div className="analytics-search-container" ref={dropdownRef}>
            <TextField
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              placeholder={`Search ${entityLabel} by name or email...`}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 500,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "&.Mui-focused fieldset": { borderColor: "var(--admin-page--color, #7c3aed)" },
                },
              }}
            />
            {showDropdown && (
              <SearchDropdown
                results={searchResults}
                loading={searchLoading}
                onSelect={handleEntitySelect}
              />
            )}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="analytics-loading">
            <CircularProgress />
          </div>
        ) : tab !== "platform" && !selectedEntity ? (
          <div className="analytics-loading">
            <h3 style={{ color: "#9ca3af" }}>
              Search and select a {entityLabel.toLowerCase()} to view analytics
            </h3>
          </div>
        ) : !d ? (
          <div className="analytics-loading">
            <h3 style={{ color: "#9ca3af" }}>
              {view === "daily" ? `No data for ${date}` : "No lifetime data yet"}
            </h3>
          </div>
        ) : tab === "platform" ? (
          <PlatformCards d={d} view={view} date={date} onCardClick={handleCardClick} />
        ) : (
          <EntityCards d={d} entityType={tab} view={view} />
        )}
      </div>
    </>
  );
};

export default Analytics;
